import type { Theme } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { SvgColor } from 'src/components/svg-color';

import { getNavData } from '../nav-config-dashboard';

type ResolveNavDataProps = {
  pathname: string;
  searchParams: URLSearchParams;
  hasOrganizationLogo: boolean;
  hasJobs: boolean;
  isAiInterviewsAvailable: boolean
};

const icon = (name: string) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} />;

const ICONS = {
  user: icon('ic-user'),
  file: icon('ic-file'),
  menuItem: icon('ic-menu-item'),
  kanban: icon('ic-kanban'),
  calendar: icon('ic-calendar'),
  analytics: icon('ic-analytics'),
  settings: icon('ic-settings'),
  folder: icon('ic-folder'),
  mail: icon('ic-mail'),
  banking: icon('ic-banking'),
  compare: icon('ic-compare'),
  external: icon('ic-external'),
};

function getContextBackItem(backPath: string) {
  return {
    title: 'Back to Main Menu',
    path: backPath,
    icon: ICONS.menuItem,
    slotProps: {
      sx: {
        border: (theme: Theme) => `1px solid ${theme.vars.palette.primary.lighter}`,
      }
    },
  };
}

export function buildMainMenuSwitchPath(pathname: string, searchParams: URLSearchParams): string {
  const paramsWithMainNav = new URLSearchParams(searchParams.toString());
  paramsWithMainNav.set('nav', 'main');
  return `${pathname}?${paramsWithMainNav.toString()}`;
}

export function getJobsSidebarNavData(jobId: string, backPath: string): NavSectionProps['data'] {
  const query = jobId ? `?jobId=${jobId}` : '';

  return [
    {
      items: [getContextBackItem(backPath)],
    },
    {
      subheader: 'Job Navigation',
      items: [
        { title: 'Job Details', path: `${paths.jobs.jobDetails}${query}`, icon: ICONS.file },
        { title: 'Candidates', path: `${paths.jobs.candidates}${query}`, icon: ICONS.user },
        { title: 'Interview Evaluation', path: `${paths.jobs.interviewEvaluation}${query}`, icon: ICONS.analytics },
        { title: 'Workflow', path: `${paths.jobs.workflow}${query}`, icon: ICONS.kanban },
        { title: 'Interview Hub', path: `${paths.jobs.interviewHub}${query}`, icon: ICONS.calendar },
        { title: 'Comparison Hub', path: `${paths.jobs.compare}${query}`, icon: ICONS.compare },
      ],
    },
  ];
}

export function getOrgSidebarNavData(backPath: string): NavSectionProps['data'] {
  return [
    {
      items: [getContextBackItem(backPath)],
    },
    {
      subheader: 'Organization Settings',
      items: [
        { title: 'Basic Info', path: paths.orgSettings.info, icon: ICONS.settings },
        { title: 'Job Board Embed', path: paths.orgSettings.jobBoardEmbed, icon: ICONS.external },
        { title: 'Billing', path: paths.orgSettings.billing, icon: ICONS.banking },
        { title: 'Members', path: paths.orgSettings.members, icon: ICONS.user },
        { title: 'Invitations', path: paths.orgSettings.invitations, icon: ICONS.mail },
        { title: 'AI Knowledge Base', path: paths.orgSettings.knowledgeBase, icon: ICONS.folder },
        { title: 'AI Persona', path: paths.orgSettings.aiPersona, icon: ICONS.analytics },
        {
          title: 'Email Templates',
          path: paths.orgSettings.notifications.emailTemplates,
          icon: ICONS.file,
        },
      ],
    },
  ];
}

export function resolveDashboardNavData({
  pathname,
  searchParams,
  hasOrganizationLogo,
  hasJobs,
  isAiInterviewsAvailable = true
}: ResolveNavDataProps): NavSectionProps['data'] {
  const currentPath = pathname.replace(/\/+$/, '') || '/';
  const isJobContext = currentPath.startsWith('/jobs/') && currentPath !== '/jobs';
  const isOrgContext =
    currentPath.startsWith('/organization-settings/') && currentPath !== '/organization-settings';
  const isMainMenuMode = searchParams.get('nav') === 'main';

  if (isMainMenuMode) {
    return getNavData(hasOrganizationLogo, hasJobs, isAiInterviewsAvailable);
  }

  const mainMenuSwitchPath = buildMainMenuSwitchPath(pathname, searchParams);

  if (isJobContext) {
    const jobId = searchParams.get('jobId') ?? '';
    return getJobsSidebarNavData(jobId, mainMenuSwitchPath);
  }

  if (isOrgContext) {
    return getOrgSidebarNavData(mainMenuSwitchPath);
  }

  return getNavData(hasOrganizationLogo, hasJobs, isAiInterviewsAvailable);
}
